import { useState, useCallback } from 'react';
import { duplicateService, DuplicateMatch, DuplicateScanResult } from '../lib/services/duplicateService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export function useDuplicateDetection() {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<DuplicateScanResult | null>(null);
  const [realTimeMatches, setRealTimeMatches] = useState<DuplicateMatch[]>([]);
  const [checking, setChecking] = useState(false);

  const scanAll = useCallback(async () => {
    if (!user?.organizationId) return;
    setScanning(true);
    try {
      const result = await duplicateService.scanAll(user.organizationId);
      setScanResult(result);
      toast.success(`${result.duplicates.length} duplikáció találva`);
    } catch (error) {
      console.error('Error scanning duplicates:', error);
      toast.error('Hiba a duplikációk keresésekor');
    } finally {
      setScanning(false);
    }
  }, [user]);

  const checkRealTime = useCallback(async (
    data: { email?: string; phone?: string; taxNumber?: string; companyName?: string; firstName?: string; lastName?: string },
    excludeId?: string
  ) => {
    if (!user?.organizationId) return;
    setChecking(true);
    try {
      const matches = await duplicateService.checkMultiple(data, user.organizationId, excludeId);
      setRealTimeMatches(matches);
    } catch (error) {
      console.error('Error checking duplicates:', error);
    } finally {
      setChecking(false);
    }
  }, [user]);

  const merge = useCallback(async (primaryId: string, secondaryId: string, mergedData: any) => {
    try {
      await duplicateService.merge(primaryId, secondaryId, mergedData);
      toast.success('Ügyfelek sikeresen összevonva');
      // Re-scan after merge
      if (user?.organizationId) {
        scanAll();
      }
    } catch (error) {
      console.error('Error merging customers:', error);
      toast.error('Hiba az összevonáskor');
      throw error;
    }
  }, [user, scanAll]);

  return {
    scanning,
    scanResult,
    realTimeMatches,
    checking,
    scanAll,
    checkRealTime,
    merge,
  };
}
